import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { SITE } from "../lib/site";
import { PageHero } from "./Services";

const PAGES = {
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    metaDescription:
      "Privacy policy for Rhawn Auto Tag, a privately owned third-party processing agency in Philadelphia PA.",
    sections: [
      {
        h: "Information We Collect",
        p: "Rhawn Auto Tag collects only the information necessary to process your Pennsylvania title, tag, notary, or insurance transaction — this may include your name, address, phone number, email, driver's license, VIN, insurance details, and payment information. We collect this information in person at our counter or via forms you complete on our website.",
      },
      {
        h: "How We Use Your Information",
        p: "Personal information is used exclusively to (a) process your paperwork through the applicable state agency and/or an insurance carrier, (b) contact you about your service, and (c) fulfill our legal obligations as a third-party processing agency. We do not sell, rent, or trade your personal information.",
      },
      {
        h: "Information Sharing",
        p: "We share information only with PennDOT, the Pennsylvania Department of Transportation, insurance carriers you request quotes from, and government agencies as required by law. Third-party service providers who help us operate our website may access limited information only to perform their function.",
      },
      {
        h: "Data Security",
        p: "We use reasonable administrative, physical, and electronic safeguards to protect your information. Payment card data is processed through PCI-compliant terminals and is not stored on our systems.",
      },
      {
        h: "Your Rights",
        p: "You may request access to, correction of, or deletion of the personal information we hold about you (subject to legal record-retention requirements). Contact us at info@rhawntag.com or call 267-355-0430.",
      },
      {
        h: "Cookies & Website Analytics",
        p: "Our website may use cookies to remember preferences and measure aggregate usage. You may disable cookies in your browser at any time.",
      },
      {
        h: "Contact",
        p: "Questions about this policy? Contact Rhawn Auto Tag at 7915 Bustleton Ave, 1st Floor, Philadelphia, PA 19152 · 267-355-0430 · info@rhawntag.com.",
      },
    ],
  },
  terms: {
    eyebrow: "Legal",
    title: "Terms & Conditions",
    metaDescription:
      "Terms and conditions for services provided by Rhawn Auto Tag in Philadelphia PA.",
    sections: [
      {
        h: "Acceptance of Terms",
        p: "By using our services or website, you agree to these terms. If you do not agree, please do not use our services.",
      },
      {
        h: "Services & Fees",
        p: "Rhawn Auto Tag is a privately owned, third-party processing agency providing paperwork assistance, notary and insurance-brokerage services. We are not a government entity and are not affiliated with the Pennsylvania Department of Transportation or any DMV. State fees are set by the Commonwealth and are collected on their behalf. Our service fees are shown on a posted price list and are non-refundable once work has begun.",
      },
      {
        h: "Customer Responsibilities",
        p: "You are responsible for the accuracy of information you provide and for supplying required original documents (title, ID, insurance, etc.). Rhawn Auto Tag is not liable for delays or rejections caused by incomplete, illegible, or invalid customer documentation.",
      },
      {
        h: "PennDOT Authority",
        p: "As a third-party processing agency, we prepare and submit paperwork on your behalf; final acceptance of any application is at the applicable state agency's discretion. Processing times outside our control may apply.",
      },
      {
        h: "Insurance Brokerage",
        p: "Insurance quotes and policies are placed with licensed Pennsylvania carriers. Coverage terms, exclusions, and premiums are governed by the carrier's policy documents. Rhawn Auto Tag is not the insurer.",
      },
      {
        h: "Website Use",
        p: "The website is provided \"as is\" for informational purposes. We do not warrant that all information is complete or error-free. Third-party links are provided for convenience only.",
      },
      {
        h: "Limitation of Liability",
        p: "To the maximum extent permitted by Pennsylvania law, Rhawn Auto Tag is not liable for indirect, incidental, or consequential damages arising from our services or your use of this website.",
      },
      {
        h: "Governing Law",
        p: "These terms are governed by the laws of the Commonwealth of Pennsylvania.",
      },
      {
        h: "Contact",
        p: "Rhawn Auto Tag · 7915 Bustleton Ave, 1st Floor, Philadelphia, PA 19152 · 267-355-0430 · info@rhawntag.com.",
      },
    ],
  },
};

export default function Legal() {
  const { doc } = useParams();
  const data = PAGES[doc];

  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") || "";
    document.title = `${data.title} — ${SITE.short} | Philadelphia PA`;
    if (meta && data.metaDescription) meta.setAttribute("content", data.metaDescription);
    return () => {
      document.title = prevTitle;
      if (meta) meta.setAttribute("content", prevDesc);
    };
  }, [data]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <>
      <PageHero eyebrow={data.eyebrow} title={data.title} image={SITE.images.paperwork} />
      <section className="py-20 md:py-28 bg-[#FBF9F5]" data-testid={`legal-${doc}`}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-10">
          <p className="text-xs uppercase tracking-[0.24em] text-[#0A192F]/60">
            Last updated · {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </p>
          <div className="mt-10 space-y-10">
            {data.sections.map((s) => (
              <div key={s.h}>
                <h2 className="font-serif text-2xl md:text-3xl text-[#0A192F] leading-tight">
                  {s.h}
                </h2>
                <p className="mt-4 text-[#0A192F]/75 text-base leading-relaxed">
                  {s.p}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
